/**
 * Shared-guide state vocabulary + receipt/toggle primitives.
 *
 * The couple's frozen shared artifact is never a reader of its own: since the
 * hybrid reader landed, the `/library` panel renders BOTH artifacts through
 * `guideDrillHelper`'s merged reader, and this module supplies the pieces that
 * are specific to the shared half — the `/library` card's state word, the
 * viewer's OWN receipt view, and the enable/disable switch that is the only
 * revoke/re-enable UI shared guides get (the registry page deliberately has
 * none). The reader's masthead is NOT among them: it carries no state word,
 * because the document says every state in prose (the state plate and the
 * Together trailer) rather than in chrome.
 *
 * Deliberately imports nothing from `guideDrillHelper`: the merged reader
 * imports THIS module, so the dependency runs one way only.
 *
 * A3 (non-negotiable): this renders ONLY the viewer's own receipt. Nothing
 * here reads or names the partner's per-unit choices — `confirmedByPartner`
 * is a boolean ceiling, never surfaced as anything more specific. The
 * floored-out "still preparing" copy (decision F) never asserts a cause.
 *
 * @module components/sharedGuideDrillHelper
 */

import { escapeHtml } from '../core/utils.js';
import { renderSwitch } from './uiSwitch.js';
import { formatLongDate } from './partnerNotesDrillHelper.js';

/**
 * `viewerState` -> the `/library` card's Together-pill vocabulary
 * (`guidePartMark.renderPartChip` via `sharedGuideCardQualifier`): a lowercase
 * descriptive `qualifier` per state — `waiting` → "waiting" (not built yet),
 * `sealed` → "writing" (being generated), `withdrawn` → "hidden" (built, but a
 * visibility switch — either partner's — is off), `pending-review` → '' because
 * that state is ACTIONABLE and the card says it on its right rail ("Needs
 * review") instead of on the pill.
 *
 * The card never takes a partner name: the one thing it must keep
 * indistinguishable is plain `waiting` vs floored-out waiting (see
 * `isFlooredOutWaiting`), because naming floored would reveal how little the
 * partner shared.
 */
const VIEWER_STATE_META = {
  waiting: { qualifier: 'waiting' },
  'pending-review': { qualifier: '' },
  sealed: { qualifier: 'writing' },
  withdrawn: { qualifier: 'hidden' }
};

/**
 * The floored-out sub-branch (design §13 D4 decision F): both partners have
 * confirmed (`viewerState === 'waiting'` only ever occurs once the viewer's
 * OWN confirmation has landed; `confirmedByPartner` is the other conjunct),
 * yet the artifact hasn't sealed — either it's still mid-seal (a quiet
 * `not_ready` from a serialization conflict) or the registry's evidence
 * floor wasn't met. Both cases are indistinguishable from the client and
 * must read identically per R9's naming rule: never assert a cause, never
 * name a unit, never imply the partner withheld anything (A3). Card word and
 * reader trailer both route through this one predicate so they can't drift.
 * @param {Object} sharedGuide - Shared-guide manifest item
 * @returns {boolean}
 */
export function isFlooredOutWaiting(sharedGuide) {
  return sharedGuide?.viewerState === 'waiting' && sharedGuide?.confirmedByPartner === true;
}

/**
 * Whether `sections` is a non-empty array of renderable guide sections — every
 * element a non-null object carrying a string `type`. The ready-branch guard
 * (review CR-3): a `ready` row's `content` can already be malformed in ways
 * the backend contract didn't originally reject (a `null` element, a bare
 * string, an object with no `type`), and `guideDrillHelper`'s renderers
 * assume a real section object — `resolveSectionContract` and the carrier
 * renderers throw or misbehave on anything else. Checking element shape here
 * (rather than wrapping the render call in try/catch) keeps the fail-soft
 * "being prepared" fallback declarative: a malformed element fails the guard
 * before rendering ever starts, so the reader never sees a crash.
 * @param {*} sections
 * @returns {boolean}
 */
export function hasRenderableSections(sections) {
  return Array.isArray(sections) && sections.length > 0 &&
    sections.every(s => s !== null && typeof s === 'object' && typeof s.type === 'string');
}

/**
 * The `/library` card's Together-pill qualifier for a shared-guide manifest
 * item: '' when the couple's reading is readable (`ready ∧ sealed`) — a reader
 * actually looking at content gets no preparing word at all — or when the
 * state is actionable and belongs on the card's rail (`pending-review`);
 * otherwise the state's `qualifier` from `VIEWER_STATE_META` — `waiting` /
 * `writing` / `hidden`.
 *
 * Floored-out waiting needs no branch: its `viewerState` is `waiting`, so it
 * lands on "waiting" identically to plain waiting BY CONSTRUCTION — the card
 * must never distinguish the two (A3: naming floored would reveal the
 * partner's content choices). Never takes a partner name.
 *
 * An unrecognised (future) `viewerState` defaults to "waiting", not '': on the
 * card every pill is filled, so an empty qualifier reads "readable". The
 * fail-soft direction has to stay "not readable" — a state the client does not
 * know is, by definition, not one it can open — and "waiting" is the qualifier
 * that says exactly that without asserting a cause.
 * @param {Object} sharedGuide - Shared-guide manifest item
 * @returns {string} '' | 'waiting' | 'writing' | 'hidden'
 */
export function sharedGuideCardQualifier(sharedGuide) {
  if (sharedGuide?.status === 'ready' && sharedGuide?.viewerState === 'sealed') return '';
  const entry = VIEWER_STATE_META[sharedGuide?.viewerState];
  return entry ? entry.qualifier : 'waiting';
}

/** Human label for a receipt row's depth value. */
function depthLabel(depth) {
  if (depth === 'full') return 'Full';
  if (depth === 'basic') return 'Basic';
  return escapeHtml(depth || '');
}

/**
 * Display name for a receipt row's `unitId`. The real per-dimension labels
 * (`PARTNER_GESTALT_DIMENSIONS`) live server-side only
 * (`backend/src/services/insights/partnerGestaltDimensions.js`) and are not
 * exposed on this manifest; the frontend's only existing label source for
 * these ids (`partnerSharingFixtures.js`'s `DIMS` map) is an explicitly
 * dev-only, unexported mock harness that must stay zero-network and is not a
 * legitimate import for production code. So per the plan's documented
 * fallback: prettify the unitId itself (`emotional_intimacy` -> "Emotional
 * intimacy") rather than reaching into either of those.
 * @param {string} unitId
 * @returns {string}
 */
function unitDisplayName(unitId) {
  const words = String(unitId || '').trim().replace(/[_-]+/g, ' ');
  if (!words) return 'This area';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Whether a receipt row is the one combination that ever produces less than
 * what was chosen: chosen `full`, consumed `basic` (a basic-unavailable-at-
 * the-time case, not a later downgrade). Upgrades/other combinations are
 * silent (mirrors `SharingDetailPanel`'s "upgrades are silent" rule).
 * @param {{chosenDepth: string, consumedDepth: string}} item
 * @returns {boolean}
 */
function isLevelDown(item) {
  return item?.chosenDepth === 'full' && item?.consumedDepth === 'basic';
}

/**
 * One receipt row: unit name + the depth as a grey fact pill. A level-down
 * row shows both depths in the pill — the chosen one struck through, the
 * consumed one beside it — and says nothing else; the one §9 sentence is
 * rendered ONCE under the list (`renderReceiptSection`), not per row.
 * @param {{unitId: string, chosenDepth: string, consumedDepth: string}} item
 * @returns {string} HTML string
 */
function renderReceiptRow(item) {
  const depth = isLevelDown(item)
    ? `<s>${depthLabel(item.chosenDepth)}</s> ${depthLabel(item.consumedDepth)}`
    : depthLabel(item?.chosenDepth);
  return `
    <li class="shared-guide-receipt__row">
      <span class="shared-guide-receipt__unit">${escapeHtml(unitDisplayName(item?.unitId))}</span>
      <span class="shared-guide-receipt__depth">${depth}</span>
    </li>
  `;
}

/**
 * The receipt view: an in-container eyebrow, one row per surviving unit in
 * the viewer's own receipt, and — once, only when some row was levelled
 * down — the sanctioned §9 footnote. '' when there is no receipt yet
 * (`receipt` is null while the artifact is still `pending` — i.e.
 * `viewerState` `waiting`/`pending-review`).
 *
 * The "Change what you share" link is not here: it belongs to the colophon's
 * action row (`renderTogetherColophon`), beside the switch.
 *
 * @param {Object} sharedGuide - Shared-guide manifest item
 * @param {Object} [opts]
 * @param {string|null} [opts.id=null] - Element id for the receipt block
 * @returns {string} HTML string
 */
export function renderReceiptSection(sharedGuide, { id = null } = {}) {
  if (!Array.isArray(sharedGuide?.receipt) || sharedGuide.receipt.length === 0) return '';
  const note = sharedGuide.receipt.some(isLevelDown)
    ? `<p class="shared-guide-receipt__note">Where you shared at different depths, the shorter version was used.</p>`
    : '';
  return `
    <div class="shared-guide-receipt"${id ? ` id="${escapeHtml(id)}"` : ''}>
      <p class="shared-guide-receipt__label">What you shared</p>
      <ul class="shared-guide-receipt__list">${sharedGuide.receipt.map(renderReceiptRow).join('')}</ul>
      ${note}
    </div>
  `;
}

/**
 * The enable/disable toggle — the viewer's own conjunct of the bilateral
 * visibility gate, and the ONLY revoke/re-enable UI shared guides get (the
 * registry page deliberately has none — addendum, owner call 2026-08-25).
 * A short label and the shared small switch, sized for the colophon's action
 * row rather than a settings page; wired in `wireEnableToggle` below rather
 * than through `data-action` delegation, since this content node is
 * privately owned by whichever panel opened it.
 * @param {Object} sharedGuide - Shared-guide manifest item
 * @returns {string} HTML string
 */
export function renderEnableToggle(sharedGuide) {
  return `
    <div class="guide-colophon__toggle">
      <span class="guide-colophon__toggle-label" id="shared-guide-toggle-label">Show in my Library</span>
      ${renderSwitch({ on: !!sharedGuide?.enabled, small: true, action: 'toggle-shared-guide-enabled', ariaLabelledby: 'shared-guide-toggle-label' })}
    </div>
  `;
}

/**
 * The Together colophon: the plate that closes the couple's reading (or, when
 * that reading is not rendered, closes the You part as the trailer). It is
 * provenance and control, not prose — which is why it is a bordered plate in
 * the zone-header voice rather than another numbered section: a heading, one
 * line of fact, the viewer's OWN receipt where one exists, and an action row
 * holding the standing Partner-page link and the
 * show/hide switch — the only revoke/re-enable UI shared guides have, so the
 * plate renders in EVERY state.
 *
 * A3: the ready lede states when and from what the reading was written and
 * that it will not change; a not-ready plate carries the caller's state line
 * (facts, never causes) in place of the lede.
 *
 * @param {Object} sharedGuide - Shared-guide manifest item
 * @param {Object} [opts]
 * @param {string|null} [opts.id=null] - Element id (the Contents menu's "Sharing settings" jump target)
 * @param {string} [opts.stateHtml=''] - Pre-escaped state line for a not-ready
 *   plate; when empty the plate is the ready one and carries the lede
 * @param {string} [opts.changeLabel='Change what you share ›'] - The action
 *   row's Partner-page link text (a `pending-review` plate says "Review what
 *   you'd share ›" — nothing has been shared yet)
 * @returns {string} HTML string
 */
export function renderTogetherColophon(sharedGuide, { id = null, stateHtml = '', changeLabel = 'Change what you share ›' } = {}) {
  const ready = !stateHtml;
  const title = ready ? 'About this Together guide' : 'About the Together guide';
  const when = ready && sharedGuide?.generatedAt ? ` ${escapeHtml(formatLongDate(sharedGuide.generatedAt))}` : '';
  const lead = ready
    ? `<p class="guide-colophon__lede">Written${when} from what you each chose to share. It won't change.</p>`
    : `<p class="guide-colophon__state">${stateHtml}</p>`;
  return `
    <section class="guide-colophon"${id ? ` id="${escapeHtml(id)}"` : ''} aria-labelledby="guide-colophon-title">
      <h2 class="guide-colophon__head" id="guide-colophon-title">${title}</h2>
      ${lead}
      ${renderReceiptSection(sharedGuide)}
      <div class="guide-colophon__actions">
        <a class="guide-colophon__change" href="/partner?tab=sharing">${escapeHtml(changeLabel)}</a>
        ${renderEnableToggle(sharedGuide)}
      </div>
    </section>
  `;
}

/**
 * Wire the enable/disable toggle's click handler onto a rendered content
 * node. "Optimistic-off" per the plan: turning the switch OFF flips the
 * visible state immediately (the safe direction — revoking visibility never
 * needs to feel gated) and only reverts on a real failure; turning it ON
 * waits for `onToggleEnabled` to resolve truthy before the switch visibly
 * flips, since that direction can reveal something the write hasn't
 * actually confirmed yet.
 * @param {HTMLElement} content - Detail content element (pre-mount)
 * @param {(nextEnabled: boolean) => Promise<boolean>} [onToggleEnabled] - Host
 *   callback (InsightsSection wires this to `API.setSharedGuideEnabled`);
 *   resolves whether the write succeeded. No-op wiring when omitted.
 */
export function wireEnableToggle(content, onToggleEnabled) {
  const btn = content?.querySelector?.('[data-action="toggle-shared-guide-enabled"]');
  if (!btn || typeof onToggleEnabled !== 'function') return;

  const applyState = (on) => {
    btn.classList.toggle('switch--on', on);
    btn.classList.toggle('switch--off', !on);
    btn.setAttribute('aria-checked', on ? 'true' : 'false');
  };

  btn.addEventListener('click', async () => {
    if (btn.disabled) return;
    const current = btn.getAttribute('aria-checked') === 'true';
    const next = !current;

    if (!next) {
      applyState(false);
      btn.disabled = true;
      const ok = await onToggleEnabled(false);
      btn.disabled = false;
      if (!ok) applyState(true);
      return;
    }

    btn.disabled = true;
    const ok = await onToggleEnabled(true);
    btn.disabled = false;
    if (ok) applyState(true);
  });
}
